/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios, { AxiosRequestConfig } from 'axios';
import _ from 'lodash';
import urlJoin from 'proper-url-join';

import { endpoints } from '@ibm-watson/shared/modules/ia/util/config';
import { validation } from '@ibm-watson/shared/modules/ia/util/validation';
import type { StoreQueryParams } from '../../../../ts';
import type { Entity, NonSystemEntity } from '../../../../ts/state';
import { getAccessPrefixedUrl } from '../../../ia/util/access-control';
import { getFormatter, saveAs } from '../../util/download';

const entitiesURL = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
): string =>
  urlJoin(
    endpoints.store,
    getAccessPrefixedUrl(
      useNewIAUpdatedAccess,
      assistantId,
      `skills/${skillId}/workspace/entities`
    )
  );

const entityURL = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  entity: string
): string =>
  `${entitiesURL(skillId, useNewIAUpdatedAccess, assistantId)}/${entity}`;
const nextEntitiesURL = (next: string): string => `${endpoints.store}${next}`;

const workspaceEndpoint = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
): string =>
  urlJoin(
    endpoints.store,
    getAccessPrefixedUrl(
      useNewIAUpdatedAccess,
      assistantId,
      `skills/${skillId}`
    )
  );

const workspaceContentEndpoint = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
): string =>
  `${workspaceEndpoint(
    skillId,
    useNewIAUpdatedAccess,
    assistantId
  )}/workspace/content`;

const exportEntitiesEndpoint = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
) =>
  // NOTE THIS URL FORMAT SLIGHTLY DIFFERENT - DO NOT USE getAccessPrefixedUrl
  useNewIAUpdatedAccess
    ? `${endpoints.content}/assistants/${assistantId}/skills/${skillId}/export/entities`
    : `${endpoints.content}/skills/${skillId}/export/entities`;

const validate = (skillId: string, entity?: Entity, entities?): any[] => {
  const errors = [];
  const guidValidationErrors = validation.validateGuid(skillId);
  if (guidValidationErrors) {
    errors.push(guidValidationErrors);
  }

  if (entity) {
    let entityValidationErrors;
    if (entity.type === 'system') {
      const entityToSend = { entity: entity.entity, type: entity.type };
      entityValidationErrors = validation.validateSystemEntity(entityToSend);
    } else {
      entityValidationErrors = validation.validateEntity(entity);
    }

    if (entityValidationErrors) {
      errors.push(entityValidationErrors);
    }
  }

  if (entities) {
    const entitiesErrors = validation.validateEntities(entities);
    if (entitiesErrors) {
      errors.push(entitiesErrors);
    }
  }

  if (errors.length) {
    return errors;
  }
};

const wrapErrors = (errors: any[]): { data: { errors: any[] } } => ({
  data: { errors: _.flattenDeep(errors) }
});

type ListEntitiesParams = StoreQueryParams &
  Partial<{
    all_system_entities: boolean;
    export: boolean;
    filter: string;
    include_count: boolean;
  }>;

interface RetrievedEntities {
  entities: Entity[];
  total: number;
  nextURL: string;
}

export const list = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  extraParams?: ListEntitiesParams,
  reactivateSession?: boolean
): Promise<RetrievedEntities> => {
  const validationErrors = validate(skillId);
  if (validationErrors) {
    return Promise.reject(wrapErrors(validationErrors));
  }
  const params: ListEntitiesParams = {
    export: true,
    page_limit: 100000,
    include_count: true,
    sort: 'entity',
    include_audit: true,
    ...extraParams
  };
  const config: AxiosRequestConfig = { params };

  // FIXME, work around for https://github.ibm.com/watson-engagement-advisor/wea-backlog/issues/52786
  // Store has an issue where timestamps are not retrieved if we have `all sys entities` true
  const configNotAll: AxiosRequestConfig = {
    params: { ...params, all_system_entities: undefined }
  };
  return Promise.all([
    axios.get(
      urlJoin(entitiesURL(skillId, useNewIAUpdatedAccess, assistantId), {
        query: { reactivateSession }
      } as any),
      config
    ),
    axios.get(
      urlJoin(entitiesURL(skillId, useNewIAUpdatedAccess, assistantId), {
        query: { reactivateSession }
      } as any),
      configNotAll
    )
  ]).then(responses => {
    const entities = responses[0]?.data.entities.map(entity => {
      const entityWithAudit = _.find(responses[1]?.data.entities, {
        entity: entity.entity
      });
      return {
        ...entity,
        ...entityWithAudit
      };
    });

    return {
      entities: entities,
      total: responses[0].data.pagination.total,
      nextURL: responses[0].data.pagination.next_url
    };
  });
};

export const getOneEntity = (
  skillId: string,
  entity: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
): Promise<Entity> => {
  const skillIdValidationErrors = validate(skillId);
  if (skillIdValidationErrors) {
    return Promise.reject(wrapErrors(skillIdValidationErrors));
  }
  const config: AxiosRequestConfig = {
    params: { export: true }
  };
  return axios.get(
    entityURL(skillId, useNewIAUpdatedAccess, assistantId, entity),
    config
  );
};

/**
 * Used to fetch all SYSTEM entities, both enabled AND disabled
 * @param  {string} skillId GUID used for specifying skill
 * @param useNewIAUpdatedAccess
 * @param assistantId
 * @param  {boolean} reactivateSession Whether to reactivate the inactivity
 * session as part of this query.
 * @return {[object]} list of fetched system entities
 */
export const listSystemEntities = async (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  reactivateSession: boolean
): Promise<RetrievedEntities> => {
  return await list(
    skillId,
    useNewIAUpdatedAccess,
    assistantId,
    { all_system_entities: true },
    reactivateSession
  );
};

/**
 * Used to list all ENABLED entities
 * @param  {string} skillId UUID used for specifying user skill.
 * @param useNewIAUpdatedAccess
 * @param assistantId
 * @param  {object} params  - additional options (page_limit,..)
 * @return {[object]} list of fetched entities plus pagination data
 */
export const listAllEntities = async (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  params: ListEntitiesParams
): Promise<RetrievedEntities> => {
  const userEntitiesResponse = await list(
    skillId,
    useNewIAUpdatedAccess,
    assistantId,
    params
  );
  const sysEntitiesResponse = await list(
    skillId,
    useNewIAUpdatedAccess,
    assistantId,
    {
      all_system_entities: true,
      ...params
    }
  );

  // concatenate the user and system entities
  userEntitiesResponse.entities = userEntitiesResponse.entities.filter(
    entity => !entity.entity.startsWith('sys-')
  );
  userEntitiesResponse.entities = userEntitiesResponse.entities.concat(
    sysEntitiesResponse.entities.filter(entity => entity.source)
  );
  return userEntitiesResponse;
};

export const listEnabledSystemEntities = async (
  skillId: string,
  assistantId: string,
  reactivateSession: boolean
): Promise<RetrievedEntities> => {
  const url = urlJoin(entitiesURL(skillId, true, assistantId), {
    query: { reactivateSession }
  } as any);

  const config: AxiosRequestConfig = {
    params: {
      export: true,
      filter: 'entity::sys-*',
      page_limit: 100000
    }
  };

  const response = await axios.get(url, config);
  return {
    entities: response.data?.entities ?? [],
    total: response.data.pagination.total,
    nextURL: response.data.pagination.next_url
  };
};

export const listUserEntities = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  extraParams: ListEntitiesParams = {},
  includeSystemEntities?: boolean,
  reactivateSession?: boolean
): Promise<RetrievedEntities> => {
  const validationErrors = validate(skillId);
  if (validationErrors) {
    return Promise.reject(wrapErrors(validationErrors));
  }
  const params = {
    export: true,
    page_limit: 100000,
    include_count: true,
    sort: 'entity',
    include_audit: true,
    filter: !includeSystemEntities ? 'entity::!sys-*' : '',
    ...extraParams
  };
  const config: AxiosRequestConfig = { params };
  return axios
    .get(
      urlJoin(entitiesURL(skillId, useNewIAUpdatedAccess, assistantId), {
        query: {
          reactivateSession: reactivateSession
        }
      } as any),
      config
    )
    .then(response => {
      return {
        entities: response.data.entities,
        total: response.data.pagination.matched, // we want the total number of matched entries
        nextURL: response.data.pagination.next_url
      };
    });
};

export const filterEntities = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  dparams,
  queryString,
  allSystemEntities
): Promise<Pick<RetrievedEntities, 'entities'>> | any[] => {
  const validationErrors = validate(skillId);
  if (validationErrors) {
    return validationErrors;
  }

  let queryParam = '?export=true&include_audit=true&page_limit=100';
  if (queryString.length && queryString.charAt(0) === '@') {
    //If @ is in the queryString look for an exact match with a wildcard at the end.
    queryString = queryString.slice(1); // Remove @ since it isn't supported in the API
    queryParam += `&filter=entity::${queryString}*`;
  } else if (queryString.length) {
    queryParam += `&q=entity~${queryString}`;
  }

  const url =
    entitiesURL(skillId, useNewIAUpdatedAccess, assistantId) + queryParam;

  return axios.get(url).then(response => {
    const matches = _.filter(allSystemEntities, function (sysEnt) {
      return sysEnt.entity.indexOf(queryString) !== -1;
    });
    let entities = response.data.entities.concat(matches);
    entities = _.uniqBy(entities, 'entity');
    const allEntities = entities.sort(function (e1, e2) {
      return e1.entity.localeCompare(e2.entity);
    });

    return {
      entities: allEntities
    };
  });
};

export const listMoreEntities = async (
  next: string,
  requestParams: ListEntitiesParams = {}
): Promise<Pick<RetrievedEntities, 'entities' | 'nextURL'>> => {
  const params = Object.assign(
    {
      page_limit: 100,
      include_count: true,
      sort: 'entity'
    },
    requestParams
  );

  if (next.indexOf('include_audit') === -1) {
    params.include_audit = true;
  }
  if (next.indexOf('filter=entity::!sys-*') === -1) {
    params.filter = 'entity::!sys-*';
  }

  return axios.get(nextEntitiesURL(next), { params }).then(response => {
    return {
      entities: response.data.entities,
      nextURL: response.data.pagination.next_url
    };
  });
};

export const create = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  entity: Entity
): Promise<Entity> => {
  if ((entity as NonSystemEntity).fuzzy_match !== false) {
    (entity as NonSystemEntity).fuzzy_match = true;
  }

  const validationErrors = validate(skillId, entity);

  if (validationErrors) {
    return Promise.reject(wrapErrors(validationErrors));
  }

  return axios
    .post(entitiesURL(skillId, useNewIAUpdatedAccess, assistantId), entity)
    .then(response => response.data)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
};

export const update = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  currentEntityName: string,
  updatedEntity: Entity
): Promise<Entity> => {
  const validationErrors = validate(skillId, updatedEntity);
  if (validationErrors) {
    return Promise.reject(wrapErrors(validationErrors));
  }
  return axios
    .post(
      entityURL(skillId, useNewIAUpdatedAccess, assistantId, currentEntityName),
      updatedEntity
    )
    .then(response => response.data)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
};

export const deleteEntity = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  entity: Entity
): Promise<any> => {
  const validationErrors = validate(skillId, entity);

  if (validationErrors) {
    return Promise.reject(wrapErrors(validationErrors));
  }
  return axios
    .delete(
      entityURL(skillId, useNewIAUpdatedAccess, assistantId, entity.entity)
    )
    .then(response => response)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
};

export const deleteEntities = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  entities: Entity[]
): Promise<void> => {
  const validationErrors = validate(skillId, null, entities);
  if (validationErrors) {
    return Promise.reject(wrapErrors(validationErrors));
  }

  const url = workspaceContentEndpoint(
    skillId,
    useNewIAUpdatedAccess,
    assistantId
  );
  const value = entities.map(entity => ({ id: entity.entity }));

  const params = {
    operation: 'delete',
    resource: 'entity',
    value
  };

  return axios
    .patch(url, params)
    .then(response => response.data)
    .catch(error => Promise.reject(error));
};

// equivalent to old bulk remove
export const deleteAllEntities = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
): Promise<void> => {
  const validationErrors = validate(skillId);
  if (validationErrors) {
    return Promise.reject(wrapErrors(validationErrors));
  }

  const url = workspaceEndpoint(skillId, useNewIAUpdatedAccess, assistantId);
  const params = { workspace: { entities: [] } };
  return axios
    .post(url, params)
    .then(response => response.data)
    .catch(error => Promise.reject(error));
};

// equivalent to old bulk replace
export const replaceEntities = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  entitiesToKeep: { entity: string }[],
  sysEntityNames: string[]
): Promise<void> => {
  const validationErrors = validate(skillId);
  if (validationErrors) {
    return Promise.reject(wrapErrors(validationErrors));
  }
  const url = workspaceEndpoint(skillId, useNewIAUpdatedAccess, assistantId);
  let combinedEntities = entitiesToKeep || [];
  if (sysEntityNames && sysEntityNames.length > 0) {
    const sysEntityObjs = sysEntityNames.map(systemEntityName => ({
      entity: systemEntityName
    }));
    combinedEntities = combinedEntities.concat(sysEntityObjs);
  }

  const params = { workspace: { entities: combinedEntities } };

  return axios
    .post(url, params)
    .then(response => response.data)
    .catch(error => Promise.reject(error));
};

export const importEntities = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  file: string
): Promise<any> => {
  const validationErrors = validate(skillId);
  if (validationErrors) {
    return Promise.reject(wrapErrors(validationErrors));
  }
  const config: AxiosRequestConfig = {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  };
  const url = workspaceContentEndpoint(
    skillId,
    useNewIAUpdatedAccess,
    assistantId
  );
  const formData = new FormData();
  formData.append('entity-csv', file);
  return axios.post(url, formData, config).then(
    response => {
      return _.get(response, 'headers.location', null);
    },
    error => {
      return Promise.reject(_.get(error, 'response') || error);
    }
  );
};

export const getImportEntitiesJobInfo = (location: string): Promise<any> => {
  return axios.get(endpoints.store + location).then(response => response.data);
};

export const exportEntities = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  entities: Entity[] = [],
  invert: boolean,
  selectAll: boolean
): Promise<void> => {
  const url = exportEntitiesEndpoint(
    skillId,
    useNewIAUpdatedAccess,
    assistantId
  );
  const data = {
    all: selectAll,
    invert,
    name: 'entity',
    names: entities.map(entity => entity.entity)
  };
  return axios.post(url, data).then(response => {
    const result = getFormatter(response.data, 'entities');
    const filename = `${skillId}_entities.csv`;
    const file = new Blob([result], { type: 'text/csv; charset=UTF-8' });
    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(file, filename);
    } else {
      saveAs(URL.createObjectURL(file), filename);
    }
  });
};

export const deleteEntityValues = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  entityName: string,
  values: { value: any }[]
): Promise<void> => {
  const validationErrors = validate(skillId);
  if (validationErrors) {
    return Promise.reject(wrapErrors(validationErrors));
  }
  const url = workspaceContentEndpoint(
    skillId,
    useNewIAUpdatedAccess,
    assistantId
  );

  const value = values.map(val => ({ id: val.value }));

  const data = {
    operation: 'delete',
    resource: 'value',
    references: {
      entity_id: entityName
    },
    value
  };

  return axios
    .patch(url, data)
    .then(response => response.data)
    .catch(error => Promise.reject(error));
};
